import { useEffect, useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Box, Clock, Hourglass, Search, Star, Wine } from "lucide-react";
import usePagination from "@/hooks/useSyncedPagination";
import ExpressFullIcon from "@/assets/icons/express-full.svg?react";
import { ActionDataBo } from "@/types/tableAction";
import { useGetAllExpeditionBo, useGetExpeditionDynamicCountsBo } from "@/api/expedition.api";
import { useGetCustoms } from "@/api/customs.api";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import type { DynamicCountsBo, GetAllExpeditionBoDto } from "@/types/api/api-types";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { QuickFilterButton } from "@/components/shared/quickFilterButton";
import { ResetButton } from "@/components/shared/resetButton";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import {
  useExpeditionsBoxFilters,
  type ExpeditionsBoxFilters,
} from "@/components/backOffice/expeditionsBox/useExpeditionsBoxFilters.reducer";
import { dateRangeToParams } from "@/components/backOffice/shared/dateFilter.util";
import { WeekFilter } from "@/components/backOffice/expeditionsBox/weekFilter";
import { PendingExpeditionActionModals } from "@/components/backOffice/pendingExpeditions/pendingExpeditionActionModals";
import {
  packagingIdentifierColumn,
  envoiColumn,
  docsColumn,
  comColumn,
  pickupDateColumn,
  clientColumn,
  clientDestColumn,
  countryColumn,
  btlsVolColumn,
  incotermColumn,
  packagingColumn,
} from "@/components/backOffice/shared/expeditionColumns";
import { BOX_TAB_QUERY_FILTER, PackagingStatusBox, type PackagingTab } from "./packagingStatusBox";

type Props = {
  tab: PackagingTab;
  onTabChange: (tab: PackagingTab) => void;
};

export const PackagingExpeditionsBoxTab = ({ tab, onTabChange }: Props) => {
  const { t } = useTranslation("boLogistics");
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = usePagination("packagingExpeditionsBox");

  const { data: customs = [] } = useGetCustoms();

  const { filters, debouncedSearch, actions } = useExpeditionsBoxFilters();

  const dateFilter = useMemo(
    () => dateRangeToParams("pickupDate", filters.pickupDateAt),
    [filters.pickupDateAt],
  );

  const queryFilter = useMemo<GetAllExpeditionBoDto>(
    () => ({
      ...BOX_TAB_QUERY_FILTER,
      ...dateFilter,
      ...(filters.isCustomQuotation ? { quotationType: ExpeditionTypeEnum.CUSTOM_QUOTATION } : {}),
      ...(filters.isLate ? { isLate: true } : {}),
      ...(filters.isExpress ? { isExpress: true } : {}),
      ...(filters.isDelayed ? { deliveryFromDateAfter: "1970-01-01T00:00:00.000Z" } : {}),
      ...(filters.packagingType ? { packagingType: filters.packagingType } : {}),
      ...(filters.isManyBottles ? { isManyBottles: true } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [
      dateFilter,
      filters.isCustomQuotation,
      filters.isLate,
      filters.isExpress,
      filters.isDelayed,
      filters.packagingType,
      filters.isManyBottles,
      debouncedSearch,
    ],
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [queryFilter, setPagination]);

  const { data: dynamicCounts } = useGetExpeditionDynamicCountsBo(queryFilter);

  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    ...queryFilter,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);

  const columns = useMemo(
    () => [
      packagingIdentifierColumn(setActionData),
      envoiColumn(),
      docsColumn(),
      comColumn(setActionData),
      pickupDateColumn({
        header: t("packagingBoxTable.pickupDate"),
        showDeliveryBadge: true,
        showLate: true,
      }),
      clientColumn(),
      clientDestColumn(),
      countryColumn(customs),
      btlsVolColumn(),
      incotermColumn({ header: t("packagingBoxTable.incoterm") }),
      packagingColumn(),
    ],
    [setActionData, customs, t],
  );

  return (
    <>
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row md:items-center">
        <PackagingStatusBox tab={tab} onTabChange={onTabChange} />
      </div>
      <div className="flex flex-wrap gap-4">
        <div className="w-72">
          <UnifiedInput
            inputType="text"
            leftElement={<Search />}
            value={filters.search}
            onChange={(e) => actions.setSearch(String(e || ""))}
            className="placeholder:text-[#94A3B8]"
            placeholder={t("packagingFilters.searchPlaceholder")}
          />
        </div>

        <PackagingQuickFilters filters={filters} actions={actions} counts={dynamicCounts} />
        <ResetButton onReset={actions.resetTableFilters} className="ml-auto self-center" />
      </div>
      <div className="rounded-xl border-1 bg-white p-4 sm:p-8">
        <WeekFilter
          currentWeekStart={filters.weekStart}
          onWeekStartChange={actions.setWeekStart}
          dateAt={filters.pickupDateAt}
          dateFilterType={filters.pickupDateFilterType}
          onDaySelect={actions.setPickupDay}
          onWeekSelect={actions.setPickupWeek}
          onRangeSelect={actions.setPickupDateRange}
          dateField="pickupDate"
          countFilter={BOX_TAB_QUERY_FILTER}
        />
      </div>
      <DataTable
        data={expeditionsData}
        columns={columns}
        rowCount={expeditions?.count || 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        autoResetPageIndex={false}
        isLoading={isExpeditionsLoading}
        skeletonRowClassName="h-18"
        rowClassname={() => "h-18 bg-white"}
        headerClassName="bg-gray-300"
        headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
        getRowCanExpand={() => true}
      />
      <PendingExpeditionActionModals actionData={actionData} setActionData={setActionData} />
    </>
  );
};

type QuickFiltersProps = {
  filters: ExpeditionsBoxFilters;
  actions: ReturnType<typeof useExpeditionsBoxFilters>["actions"];
  counts?: DynamicCountsBo;
};

const PackagingQuickFilters = ({ filters, actions, counts }: QuickFiltersProps) => {
  const { t } = useTranslation("boLogistics");
  const isWoodBox = filters.packagingType === PackagingFilterEnum.WOODBOX;

  return (
    <div className="flex w-fit flex-row flex-wrap gap-1">
      <QuickFilterButton
        icon={<Clock className="size-4" />}
        label={t("packagingFilters.late")}
        count={counts?.late}
        active={filters.isLate}
        onClick={actions.toggleLate}
        colorClass="text-rose-500"
        activeBg="border-rose-500 bg-rose-100"
      />
      <QuickFilterButton
        icon={<ExpressFullIcon className="size-5" />}
        label={t("packagingFilters.express")}
        count={counts?.express}
        active={filters.isExpress}
        onClick={actions.toggleExpress}
        colorClass="text-[#3562f8]"
        activeBg="border-[#3562f8] bg-[#3562f8]/10"
      />
      <QuickFilterButton
        icon={<Star className="size-4" />}
        label={t("packagingFilters.customQuotation")}
        count={counts?.customQuotation}
        active={filters.isCustomQuotation}
        onClick={actions.toggleCustomQuotation}
        colorClass="text-purple-500"
        activeBg="border-purple-500 bg-purple-100"
      />
      <QuickFilterButton
        icon={<Hourglass className="size-4" />}
        label={t("packagingFilters.delayed")}
        count={counts?.delayed}
        active={filters.isDelayed}
        onClick={actions.toggleDelayed}
        colorClass="text-green-600"
        activeBg="border-green-500 bg-green-100"
      />
      <QuickFilterButton
        icon={<Box className="size-4" />}
        label={t("packagingFilters.woodBox")}
        count={counts?.woodBox}
        active={isWoodBox}
        onClick={() => actions.setPackagingType(isWoodBox ? null : PackagingFilterEnum.WOODBOX)}
        colorClass="text-orange-900"
        activeBg="border-yellow-800 bg-orange-700/30"
      />
      <QuickFilterButton
        icon={<Wine className="size-4" />}
        label={t("packagingFilters.manyBottles")}
        count={counts?.manyBottles}
        active={filters.isManyBottles}
        onClick={actions.toggleManyBottles}
        colorClass="text-teal-600"
        activeBg="border-teal-500 bg-teal-100"
      />
    </div>
  );
};
